import { useState } from 'react'
import { Form, FormSpy } from 'react-final-form'

import ConfirmationModal from 'common/components/confirmationModal'
import Drawer from 'common/components/drawer'
import useDisablePointerEventsWhenModalVisible from 'common/components/drawer/useDisablePointerEventsWhenModalVisible'
import { AdvancedLanguageRule } from 'common/interfaces/brandVoices'

import type { AdvancedLanguageRuleBody } from '../../api/api'
import useEditAdvancedLanguageRuleMutation from '../../api/mutations/useEditAdvancedLanguageRuleMutation'

import AdvancedLanguageCancelButton from './AdvancedLanguageCancelButton'
import AdvancedLanguageSubmitButton from './AdvancedLanguageSubmitButton'
import FormContent from './FormContent'

type Props = {
  initialValues: AdvancedLanguageRule
  isOpen: boolean
  onOpenChange: (value: boolean) => void
}

const EditAdvancedLanguageRulesDrawer = ({
  initialValues,
  isOpen,
  onOpenChange,
}: Props) => {
  const { mutate, isPending: isLoading } = useEditAdvancedLanguageRuleMutation()
  const [isFormDirty, setFormDirty] = useState<boolean>(false)
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false)

  const handleSave = (values: AdvancedLanguageRuleBody) => {
    mutate(
      { ruleId: initialValues.id, data: values },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  useDisablePointerEventsWhenModalVisible(isConfirmationVisible)

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title="Brand voice instruction"
        dismissible={!isConfirmationVisible}
        onOpenChange={onOpenChange}
        content={
          <div className="w-219 flex flex-col h-full">
            <Form
              initialValues={
                initialValues
                  ? {
                      rule: initialValues.rule,
                      examples: initialValues.examples,
                    }
                  : undefined
              }
              onSubmit={handleSave}
              render={({ handleSubmit }) => {
                return (
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col flex-1 h-full"
                  >
                    <FormSpy
                      onChange={(props) => {
                        const dirtyFields = Object.keys(props.dirtyFields)

                        setFormDirty(dirtyFields.length > 0)
                      }}
                    />

                    <FormContent
                      title="Brand voice instruction"
                      description="Use brand voice instructions to keep language generation on-brand."
                    />

                    <div className="flex flex-none justify-end mt-auto w-full py-6 border-t border-gold-400 gap-3">
                      <AdvancedLanguageCancelButton
                        data-testid="edit-advanced-language-cancel-button"
                        onClick={() => {
                          if (isFormDirty) {
                            setIsConfirmationVisible(true)
                          } else {
                            onOpenChange(false)
                          }
                        }}
                      />
                      <AdvancedLanguageSubmitButton
                        data-testid="edit-advanced-language-submit-button"
                        isLoading={isLoading}
                      >
                        Done
                      </AdvancedLanguageSubmitButton>
                    </div>
                  </form>
                )
              }}
            />
          </div>
        }
      />
      <ConfirmationModal
        title="Are you sure you want to leave without saving?"
        confirmationText="Any unsaved instructions and examples will be lost."
        confirmButtonText="Yes, I want to leave"
        cancelButtonText="Go back"
        open={isConfirmationVisible}
        hasConfirmationSlider={false}
        onConfirm={() => {
          onOpenChange(false)
          setIsConfirmationVisible(false)
        }}
        onCancel={() => {
          setIsConfirmationVisible(false)
        }}
      />
    </>
  )
}

export default EditAdvancedLanguageRulesDrawer
